import {
  Component,
  Inject,
  LOCALE_ID,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterContentInit,
  AfterViewInit,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  GridComponent,
  GridDataResult,
  PageChangeEvent,
} from "@progress/kendo-angular-grid";
import { Store } from "@ngrx/store";
import { State, SortDescriptor } from "@progress/kendo-data-query";
import { Observable, Subscription } from "rxjs";
import { ReportService } from "../shared/services/report.service";
import { FilterTemplateRequestModel } from "../shared/models/filter-templates.request.model";
import { UserService } from "src/app/admin/user/shared/services/user.service";
import { ReportCommonService } from "../shared/services/report-common.service";
import { ActivatedRoute, Router } from "@angular/router";
import { getACLicenseStatus } from "src/app/state";
import { FiltersComponent } from "../filters/filters.component";
import {
  LobbyEditorGridRequest,
  LobbyEditorExportRequest,
} from "../shared/models/grid-data.model";
import {
  WindowRef,
  WindowService,
  WindowCloseResult,
} from "@progress/kendo-angular-dialog";
import { SortInfo } from "../../shared/models/queryparams/sortinfo.model";
import { SystemConfigValue } from "../../admin/system-configuration/shared/models/system-config-value";
import { FilterTemplateResponseModel } from "../shared/models/filter-template.response.model";
import { GetUserPermissionList } from "src/app/state";
import { UserPermissions } from "src/app/shared/enums/permissionType";
import { FiltersOptionsModel } from "../shared/models/filter-options-model";
import { LobbyDetailEditComponent } from "src/app/shared/modalpopup/lobby-detail-edit/lobby-detail-edit.component";
import { DetailView } from "src/app/shared/enums/detailView.enum";
import { LobbyEntriesDeleteComponent } from "src/app/shared/modalpopup/lobby-entries-delete/lobby-entries-delete.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-lobby-editor",
  templateUrl: "./lobby-editor.component.html",
  styleUrls: ["./lobby-editor.component.scss", "../shared/all.scss"],
})
export class LobbyEditorComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterContentInit
{
  isACLicenseEnabled$: Observable<boolean> = new Observable<boolean>();
  public defaultSort: SortDescriptor[] = [{ field: "addedTime", dir: "desc" }];
  public sort: SortDescriptor[] = [{ field: "addedTime", dir: "desc" }];
  public selectedKeys: number[] = [];
  public loadingEditorData = 0;
  public detailViewForm!: FormGroup;
  public pageInitialized = false;
  private windowRef?: WindowRef;
  public LastRefreshed: string = "";
  public eagerLoad = true;
  public loadingTimer: Subscription = new Subscription();
  public currentLoadingPromptState: number = 0;
  interval: any;
  public loadingPrompt: string = "";
  public initialReportExecutionComplete: boolean = false;

  public gridData: GridDataResult = { data: [], total: 0 };
  public pageSize: number = 20;
  public skip: number = 0;
  public lastReportStartDate: Date = new Date(2000, 0, 1);
  public lastReportEndDate: Date = new Date(2000, 0, 1);

  public isUserCanEditGrid: boolean = false;
  public detailViewId: number = DetailView["Lobby_Editor"];

  isReportRunning: boolean = false;
  areAllRowsExpanded: boolean = false;
  public showClearButton: boolean = true;
  public loadingDataText: string = "";
  public filterText: string = "";
  public FiltersPopupOpened = false;

  @ViewChild("grid") grid?: GridComponent;

  constructor(
    public reportService: ReportService,
    public fb: FormBuilder,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    public commonData: ReportCommonService,
    private windowService: WindowService,
    @Inject(LOCALE_ID) public locale: string,
    private store: Store<State>,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.translateService.get("_Report_Loading_Data_Text_").subscribe((res) => {
      this.loadingDataText = res;
    });
    this.isACLicenseEnabled$ = this.store.select(getACLicenseStatus);
    this.allData = this.allData.bind(this);
    this.pageInitialized = true;
    this.initialReportExecutionComplete = false;
    this.isUserCanEditGrid = false;
    this.lastReportStartDate = new Date(2000, 0, 1);
    this.lastReportEndDate = new Date(2000, 0, 1);
    this.getUserPermission();

    this.commonData.applyClicked.subscribe((s: number) => {
      if (s == this.detailViewId) {
        this.resetGridOptions();
        this.refreshSelectedTemplateDropdown();
        this.runReport(null);
      }
    });
  }
  public resetGridOptions() {
    this.skip = 0;
  }

  ngOnDestroy() {
    if (this.windowRef != null) {
      this.windowRef.close();
    }
    this.FiltersPopupOpened = false;
    if (this.commonData.applyEventSubscribed == true) {
      // this.commonData.applyClicked.unsubscribe();
      this.commonData.applyEventSubscribed = false;
    }
  }

  ngAfterViewInit() {
    this.commonData.applyEventSubscribed = false;
    window.scrollTo(0, 0);
  }

  ngAfterContentInit() {
    this.generateDetailViewForm();
    this.commonData.reportStartDate = new Date(new Date().setHours(0, 0, 0, 0));
    this.commonData.reportEndDate = new Date(new Date().setHours(0, 0, 0, 0));
    //this.commonData.reportEndDate.setDate(this.commonData.reportEndDate.getDate() - 1);
    //this.commonData.reportStartDate = new Date(this.commonData.reportEndDate.valueOf());
    // this.commonData.reportStartDate.setDate(this.commonData.reportStartDate.getDate() - 7);
    this.commonData.maxReportDate = new Date(new Date().setHours(0, 0, 0, 0));
    this.commonData.minReportDate = new Date("1/1/2000");
    this.detailViewForm.get("to")?.setValue(this.commonData.reportEndDate);
    this.detailViewForm.get("from")?.setValue(this.commonData.reportStartDate);
    this.getAllFilterTemplates();
  }

  public optionsWindowClosingEvent(closingType: boolean): void {}

  private getUserPermission() {
    this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
      if (allUserPermissions) {
        this.isUserCanEditGrid = allUserPermissions.includes(
          UserPermissions["Edit Lobby Editor Report"]
        );
      }
    });
  }

  public setActiveTemplateOnUI(s: string) {
    this.detailViewForm.get("activeFilterTemplate")?.setValue(s);
  }

  public refreshSelectedTemplateDropdown() {
    this.setActiveTemplateOnUI(this.commonData.currentTemplate.templateName);
  }

  public activeTemplateChanged(e: string) {
    var s = this.detailViewForm.get("activeFilterTemplate")?.value;
    this.commonData.SetActiveTemplate(s);
    this.commonData.ConvertCurrentTemplateToOptions(this.detailViewId);
    this.runReport(null);
  }

  public getAllFilterTemplates() {
    this.showLoader();
    let templateRequest = new FilterTemplateRequestModel();
    templateRequest.TemplateId = 0;
    templateRequest.DetailViewId = this.detailViewId;
    this.reportService
      .getDetailsViewTemplates(templateRequest)
      .subscribe((templates) => {
        this.commonData.templates = [];
        this.commonData.originalTemplates = [];
        this.commonData.templates = templates;
        const t: FilterTemplateResponseModel | undefined =
          this.getDefaultTemplate(this.commonData.templates);
        if (!t) {
          throw Error("this.getDefaultTemplate returned undefined!");
        }
        this.commonData.currentTemplate = t;
        this.setActiveTemplateOnUI(t.templateName);
        this.getFilterOptions();
        this.commonData.SortTemplate(this.commonData.templates);
        this.commonData.SortTemplate(this.commonData.originalTemplates);
        this.hideLoader();
      });
  }

  isDateDisabled(date: Date): boolean {
    return (
      date < this.commonData.minReportDate ||
      date > this.commonData.maxReportDate
    );
  }

  public fromDateChange() {
    this.commonData.reportStartDate = this.detailViewForm.get("from")?.value;
    if (this.commonData.reportStartDate > this.commonData.reportEndDate) {
      this.commonData.reportStartDate = this.commonData.reportEndDate;
      this.detailViewForm
        .get("from")
        ?.setValue(this.commonData.reportStartDate);
    }
  }

  public toDateChange() {
    this.commonData.reportEndDate = this.detailViewForm.get("to")?.value;
    if (this.commonData.reportEndDate < this.commonData.reportStartDate) {
      this.commonData.reportEndDate = this.commonData.reportStartDate;
      this.detailViewForm.get("to")?.setValue(this.commonData.reportEndDate);
    }
  }

  public getFilterOptions() {
    this.reportService.getDetailsViewFilterOptions(2).subscribe((options) => {
      this.mapOptions(options);
      this.runReport(null);
    });
  }

  public mapOptions(options: FiltersOptionsModel) {
    this.commonData.options = options;
    this.commonData.options.filteredServices = this.commonData.options.services;
    this.commonData.options.filteredAssistedBys =
      this.commonData.options.assistedBy;
    this.commonData.ConvertCurrentTemplateToOptions(this.detailViewId);
  }

  public getDefaultTemplate(data: FilterTemplateResponseModel[]) {
    var result = data.find((x) => x.templateId > 0 && x.isDefault === true);
    if (!result) {
      result = data.find((x) => x.templateId < 0);
    }
    return result;
  }

  public generateDetailViewForm() {
    this.detailViewForm = this.fb.group({
      to: [null, [Validators.required]],
      from: [null, [Validators.required]],
      activeFilterTemplate: [null, [Validators.required]],
      selectedViewDropdown: [null, []],
    });
  }
  public goToFilterOptions() {
    this.filterText = "";
    if (this.loadingEditorData == 0) {
      if (this.commonData.applyEventSubscribed == false) {
        this.commonData.applyEventSubscribed = true;
        this.commonData.scrollTop.subscribe(() => {
          window.scrollTo(0, 0);
          // this.commonData.scrollTop.unsubscribe();
        });
      }
      // Save dates
      this.commonData.reportStartDate = this.detailViewForm.get("from")?.value;
      this.commonData.reportEndDate = this.detailViewForm.get("to")?.value;
      this.windowRef = this.windowService.open({
        title: this.translateService.instant("_General_FilterOption_Text_"),
        content: FiltersComponent,
        width: 800,
        height: 950,
        top: 20,
        left: 150,
      });
      this.FiltersPopupOpened = true;
      this.windowRef.result.subscribe((result) => {
        if (result instanceof WindowCloseResult) {
          this.FiltersPopupOpened = false;
        }
      });
      this.windowRef.content.instance.showViewFilters = true;
      this.windowRef.content.instance.detailViewId = this.detailViewId;
    }
  }

  public showLoader() {
    this.loadingEditorData++;
    this.detailViewForm.disable();
  }

  public hideLoader() {
    if (this.loadingEditorData > 0) {
      this.loadingEditorData--;
    }
    if (this.loadingEditorData == 0) {
      this.detailViewForm.enable();
    }
  }

  public nextLoadingPrompt() {
    this.loadingPrompt = this.loadingPrompt + ".";
  }

  public runReportDirect() {
    this.filterText = "";
    this.runReport(null);
  }

  public runReport(searchTextGrid: string | null = null) {
    this.isReportRunning = true;
    this.selectedKeys.length = 0;
    this.currentLoadingPromptState = 0;
    this.loadingPrompt = this.loadingDataText;
    this.interval = setInterval(() => {
      this.nextLoadingPrompt();
      if (this.currentLoadingPromptState > 10) {
        this.currentLoadingPromptState = 0;
        this.loadingPrompt = this.loadingDataText;
      } else {
        this.currentLoadingPromptState++;
      }
    }, 1000);
    this.showLoader();
    if (
      this.lastReportStartDate != this.commonData.reportStartDate ||
      this.lastReportEndDate != this.commonData.reportEndDate
    ) {
      this.skip = 0;
    }
    this.commonData.reportStartDate =
      this.detailViewForm.controls["from"].value;
    this.commonData.reportEndDate = this.detailViewForm.controls["to"].value;
    if (
      this.lastReportStartDate != this.commonData.reportStartDate ||
      this.lastReportEndDate != this.commonData.reportEndDate
    ) {
      this.skip = 0;
    }
    var gridDataRequest = new LobbyEditorGridRequest(
      this.commonData.currentTemplate,
      this.commonData.reportStartDate,
      this.commonData.reportEndDate,
      { Page: this.skip / this.pageSize + 1, PageSize: this.pageSize },
      this.getSortInfo(),
      searchTextGrid
    );
    this.lastReportStartDate = this.commonData.reportStartDate;
    this.lastReportEndDate = this.commonData.reportEndDate;
    this.reportService.getLobbyEditorGrid(gridDataRequest).subscribe(
      (responseData) => {
        this.isReportRunning = false;
        this.gridData = {
          data: responseData.data,
          total: responseData.totalRowCount,
        };
        this.gridData.data.forEach((o) => {
          if (o.servicesProvided.length === 0 && o.waitTime === "00:00:00") {
            o.waitTime = o.lobbyEntryWaitTime;
          }
        });
        this.hideLoader();
        this.initialReportExecutionComplete = true;
        if (!this.areAllRowsExpanded) {
          this.collapseAllRows();
        } else {
          this.expandAllRows();
        }
      },
      (error) => {
        this.isReportRunning = false;
        this.hideLoader();
        this.initialReportExecutionComplete = true;
        console.error("Running report failed.");
        console.error(error);
      }
    );
  }

  private collapseAllRows(): void {
    if (this.grid) {
      for (let x = 0; x < this.gridData.total; x++) {
        this.grid.collapseRow(x);
      }
    }
    this.areAllRowsExpanded = false;
  }

  private expandAllRows(): void {
    if (this.grid) {
      for (let x = 0; x < this.gridData.total; x++) {
        this.grid.expandRow(x);
      }
    }
    this.areAllRowsExpanded = true;
  }

  public exportToCsv(): any {
    var gridDataRequest = new LobbyEditorExportRequest(
      this.commonData.currentTemplate,
      this.commonData.reportStartDate,
      this.commonData.reportEndDate,
      this.getSortInfo()
    );
    this.reportService
      .getLobbyEditorCsvExport(gridDataRequest)
      .subscribe((response) => {
        const contentDisposition =
          response.headers.get("Content-Disposition") ||
          response.headers.get("content-disposition");
        let filename = "LobbyEditorExport.csv";
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
        const blob = new Blob([response.body], { type: "application/csv" });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadURL;
        link.download = filename;
        link.click();
      });
  }

  public openLobbyDetailEdit(
    popUp: LobbyDetailEditComponent,
    dataItem: any
  ): void {
    popUp.dataItem = dataItem;
    popUp.open();
  }

  public openDeleteLobbyEntriesDialog(popUp: LobbyEntriesDeleteComponent) {
    popUp.open(this.selectedKeys);
  }

  onExpandCollapseToggleClick(): void {
    if (this.areAllRowsExpanded) {
      this.collapseAllRows();
    } else {
      this.expandAllRows();
    }
  }

  public pageChange({ skip, take }: PageChangeEvent): void {
    this.skip = skip;
    this.pageSize = take;

    if (this.filterText != "") this.runReport(this.filterText);
    else this.runReport(null);
  }
  /**
   * Triggers when sort changed by the user
   * @param sort
   */
  public sortChange(sort: SortDescriptor[]) {
    if (!sort || sort.length === 0 || sort[0].dir == undefined) {
      this.sort = [...this.defaultSort];
    } else {
      this.sort = sort;
    }
    this.runReport(null);
  }

  public getSortInfo(): SortInfo {
    let field = this.sort[0].field;
    let dir = this.sort[0].dir === "asc" ? "asc" : "desc";

    if (field !== this.defaultSort[0].field) {
      dir = dir === "asc" ? "desc" : "asc";
    }
    if (field.length > 2) {
      let firstLetter = field.charAt(0).toUpperCase();
      field = firstLetter + field.substring(1);
    }
    return {
      SortColumn: field,
      sortDirection: dir === "asc" ? 1 : 2,
    };
  }

  public allData = (): Observable<any> => {
    var gridDataRequest = new LobbyEditorExportRequest(
      this.commonData.currentTemplate,
      this.commonData.reportStartDate,
      this.commonData.reportEndDate,
      this.getSortInfo()
    );
    return this.reportService.getLobbyEditorExport(gridDataRequest);
  };

  public onExcelExport(e: any): void {}

  private readCastedValue(systemConfigValue: SystemConfigValue): any {
    if (systemConfigValue) {
      switch (systemConfigValue.dataType) {
        case "int":
          return parseInt(systemConfigValue.value);
        case "bool":
          return systemConfigValue.value === "1";
        default:
          return systemConfigValue.value;
      }
    }
  }

  onClearSearchTextClicked() {
    if (this.filterText != "") {
      this.filterText = "";
      this.runReport(null);
    }
  }

  onFilterTextSearchClicked() {
    const searchText = this.filterText;
    this.skip = 0;
    this.pageSize = 20;

    this.runReport(searchText);
  }

  onFilterTextEnterKeyPress(e: any) {
    e.preventDefault();
    this.onFilterTextSearchClicked();
  }

  onSelectionChange(event: any): void {
    const selected = event.selectedRows.map(
      (r: any) => r.dataItem.lobbyEntryId
    );
    const deselected = event.deselectedRows.map(
      (r: any) => r.dataItem.lobbyEntryId
    );

    this.selectedKeys = [...this.selectedKeys, ...selected];
    this.selectedKeys = this.selectedKeys.filter(
      (id) => !deselected.includes(id)
    );
  }
}
